import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarIcon } from '../components/SidebarIcon';
import '../components/DirectionsLink.css';
import './PortalSectionLayout.css';
import './PortalSectionInteractions.css';
import './NewAppointmentPage.css';
import { API_BASE_URL } from '../constants';
import { t, type TranslationKey } from '../i18n/translations';
import { header3DefinitionJSON } from './StartPage';

// /api/practitioners/titled-name/{practitionerId}
// /api/user/titled-name/{userId}

// console.log("selectedProcedureId=" + JSON.stringify(localStorage.getItem('selectedProcedureId')));

// TODO: use the one in backendInterface.ts:
interface AppointmentFull {
  id: string;
  when: string;
  duration: number;
  practitionerIds: string[];
  patientIds: string[];
  procedureIds: string[];
  status: string;
  type: string;
  description: string;
  appointmentInfoMap: Record<string, string>;
  meetingInfoMap: string[];
}

type DisplayAppointment = AppointmentFull & {
  procedureName?: string;
  procedureType?: string;
  practitionerName?: string;
};

interface Procedure {
  id: string;
  name: string;
  type: string;
  duration: number;
  [ruleMap: string]: any;
}

const formatMonth = (when: string) =>
  new Date(when).toLocaleDateString('de-DE', { month: 'short' }).toUpperCase();

const formatDay = (when: string) => new Date(when).getDate().toString().padStart(2, '0');

const formatTime = (when: string) =>
  new Date(when).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

const formatDate = (when: string) =>
  new Date(when).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });

const translateProcedureName = (procedureName?: string) => {
  if (!procedureName) return 'Behandlung';
  const translatedName = t(procedureName as TranslationKey) || procedureName;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

export function NewAppointmentPage() {
  const [appointments, setAppointments] = useState<DisplayAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const procedureCacheKey = 'procedureCache';
  const practitionerCacheKey = 'practitionerTitledNameCache';

  const readProcedureCache = () => {
    try {
      const raw = localStorage.getItem(procedureCacheKey);
      if (!raw) return {} as Record<string, Procedure>;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : ({} as Record<string, Procedure>);
    } catch (cacheError) {
      console.error('Failed to read procedure cache', cacheError);
      return {} as Record<string, Procedure>;
    }
  };

  const writeProcedureCache = (cache: Record<string, Procedure>) => {
    try {
      localStorage.setItem(procedureCacheKey, JSON.stringify(cache));
    } catch (cacheError) {
      console.error('Failed to write procedure cache', cacheError);
    }
  };

  const readPractitionerCache = () => {
    try {
      const raw = localStorage.getItem(practitionerCacheKey);
      if (!raw) return {} as Record<string, string>;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : ({} as Record<string, string>);
    } catch (cacheError) {
      console.error('Failed to read practitioner cache', cacheError);
      return {} as Record<string, string>;
    }
  };

  const writePractitionerCache = (cache: Record<string, string>) => {
    try {
      localStorage.setItem(practitionerCacheKey, JSON.stringify(cache));
    } catch (cacheError) {
      console.error('Failed to write practitioner cache', cacheError);
    }
  };

  const handleEdit = async (appointmentId: string) => {
    //console.log("handleEdit: appointmentId=" + appointmentId);

    if (!appointmentId) {
      console.error('appointmentId is undefined');
      return;
    }
    localStorage.setItem('headerDefinition', header3DefinitionJSON);
    localStorage.setItem('selectedAppointmentId', appointmentId);
    localStorage.setItem('previousPage', '/appointments');

    localStorage.setItem('selectedPractitionerId', '');
    localStorage.setItem('selectedProcedureId', '');
    localStorage.removeItem('pendingAppointmentFull');
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const apiCallFull = API_BASE_URL + '/appointments/get-full-appointment/' + appointmentId;
        const response = await fetch(apiCallFull, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const fullAppointment = await response.json();
          const practitionerIds = Array.isArray(fullAppointment?.practitionerIds)
            ? fullAppointment.practitionerIds
            : Array.isArray(fullAppointment?.practitionersIds)
              ? fullAppointment.practitionersIds
              : [];
          const procedureIds = Array.isArray(fullAppointment?.procedureIds)
            ? fullAppointment.procedureIds
            : [];
          const selectedPractitionerId = practitionerIds[0];
          const selectedProcedureId = procedureIds[0];

          if (selectedPractitionerId) {
            localStorage.setItem('selectedPractitionerId', selectedPractitionerId);
          }

          if (selectedProcedureId) {
            localStorage.setItem('selectedProcedureId', selectedProcedureId);
          }

          localStorage.setItem(
            'pendingAppointmentFull',
            JSON.stringify({ ...fullAppointment, practitionerIds, procedureIds })
          );

        } else {
          console.error('Bad full appointment response for edit=' + response.status);
        }
      } catch (fetchError) {
        console.error('Failed to load full appointment in handleEdit', fetchError);
      }
    }

    console.log("handleEdit: appointmentId=" + appointmentId + " practitionerId=" + localStorage.getItem('selectedPractitionerId'));
    console.log("handleEdit: selectedProcedureId=" + JSON.stringify(localStorage.getItem('selectedProcedureId')));

    navigate('/date-time-entry');
  };

  const neuenTerminBuchen = () => {
    navigate('/procedures');
  };

  const handleAbsagen = (appointment: DisplayAppointment) => {
    console.log('handleAbsagen: appointmentId=' + appointment.id);

    const cancellationAppointment = {
      id: appointment.id,
      when: appointment.when,
      procedureName: translateProcedureName(appointment.procedureName),
      practitionerIds: appointment.practitionerIds,
      previousPage: '/appointments',
    };

    localStorage.setItem('selectedAppointmentId', appointment.id);
    localStorage.setItem('previousPage', '/appointments');
    localStorage.setItem('pendingCancellationAppointment', JSON.stringify(cancellationAppointment));
    navigate('/cancellation', { state: { appointment: cancellationAppointment } });
  };

  const handlePastDetails = (_appointmentId: string) => {

  };

  const directions = () => {
    navigate('/contact');
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  };

  useEffect(() => {
    const fetchAppointments = async () => {

      const token = localStorage.getItem('token');
      const loginUserId = localStorage.getItem('loginUserId');

      console.log("fetchAppointments: loginUserId=" + loginUserId);

      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      try {
        const apiCall = API_BASE_URL + '/appointments/search';
        const searchRes = await fetch(apiCall, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: loginUserId }),
        });

        if (!searchRes.ok) throw new Error('Suche erfolglos');

        const appointmentIds = await searchRes.json();

        console.log("fetchAppointments: appointmentIds=" + appointmentIds);

        const appointmentPromises = appointmentIds.map(async (id: string) => {
          const apiCallFull = API_BASE_URL + '/appointments/get-full-appointment/' + id;
          const res = await fetch(apiCallFull, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (!res.ok) throw new Error(`Fehler ${id}`);
          return res.json();
        });

        const appointmentDetails: AppointmentFull[] = await Promise.all(appointmentPromises);

        console.log("fetchAppointments: appointmentDetails=" + JSON.stringify(appointmentDetails));

        const procedureCache = readProcedureCache();
        const practitionerCache = readPractitionerCache();

        console.log("fetchAppointments: practitionerCache=" + JSON.stringify(practitionerCache));

        const resolveProcedure = async (procedureId: string) => {
          const cachedProcedure = procedureCache[procedureId];
          if (cachedProcedure?.name && cachedProcedure?.type) {
            return cachedProcedure;
          }

          try {
            const response = await fetch(`${API_BASE_URL}/procedures/${procedureId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              console.log('Bad procedure response=' + JSON.stringify(response));
              return undefined;
            }

            const procedure = (await response.json()) as Procedure;
            if (procedure?.id && procedure?.name) {
              procedureCache[procedureId] = procedure;
              writeProcedureCache(procedureCache);
              return procedure;
            }
          } catch (detailError) {
            console.error('Failed to load procedure', detailError);
          }

          return undefined;
        };

        const resolvePractitionerName = async (practitionerId?: string) => {
          if (!practitionerId) {
            return undefined;
          }

          const cachedName = practitionerCache[practitionerId];
          if (cachedName) {
            return cachedName;
          }

          try {
            const response = await fetch(`${API_BASE_URL}/practitioners/titled-name/${practitionerId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              console.log(`Bad practitioner titled-name response for ${practitionerId}: ${response.status}`);
              return undefined;
            }

            const contentType = response.headers.get('content-type') || '';
            const payload = contentType.includes('application/json')
              ? await response.json()
              : await response.text();

            console.log("payload=" + JSON.stringify(payload));

            const practitionerName =
              typeof payload === 'string'
                ? payload.trim()
                : typeof payload?.titledName === 'string'
                  ? payload.titledName.trim()
                  : typeof payload?.name === 'string'
                    ? payload.name.trim()
                    : typeof payload?.fullName === 'string'
                      ? payload.fullName.trim()
                      : '';

            if (!practitionerName) {
              return undefined;
            }

            console.log("practitionerName=" + practitionerName);

            practitionerCache[practitionerId] = practitionerName;
            writePractitionerCache(practitionerCache);
            return practitionerName;
          } catch (detailError) {
            console.error('Failed to load practitioner titled name', detailError);
            return undefined;
          }
        };

        const appointmentWithProcedureNames = await Promise.all(
          appointmentDetails.map(async (appointment) => {
            const procedureId = Array.isArray(appointment.procedureIds) ? appointment.procedureIds[0] : undefined;
            const practitionerId = Array.isArray(appointment.practitionerIds) ? appointment.practitionerIds[0] : undefined;

            const [procedure, practitionerName] = await Promise.all([
              procedureId ? resolveProcedure(procedureId) : Promise.resolve(undefined),
              resolvePractitionerName(practitionerId),
            ]);

            return {
              ...appointment,
              procedureName: procedure?.name,
              procedureType: procedure?.type,
              practitionerName,
            };
          })
        );

        setAppointments(appointmentWithProcedureNames);
      } catch (err) {
        setError(`Fehler: ${err}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, []);

  const now = useMemo(() => new Date(), []);

  const futureAppointments = useMemo(() => {
    return appointments
      .filter((appointment) => new Date(appointment.when) >= now)
      .sort((a, b) => new Date(a.when).getTime() - new Date(b.when).getTime());
  }, [appointments, now]);

  const pastAppointments = useMemo(() => {
    return appointments
      .filter((appointment) => new Date(appointment.when) < now)
      .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
  }, [appointments, now]);

  if (loading) return <div className="text-center p-4">Loading appointments...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;

  return (
    <div className="portal-section-page portal-section-page--appointments portal-page-surface text-slate-900">
      <div className="portal-page-content">
        <div className="portal-page-heading mb-10">
          <h1 className="text-3xl font-semibold text-slate-900">Meine Termine</h1>
          <p className="portal-appointments__subtitle mt-2 text-sm text-slate-400">
            Verwalten Sie Ihre anstehenden und vergangenen Besuche.
          </p>
        </div>

        <button
          type="button"
          onClick={neuenTerminBuchen}
          className="portal-section-card portal-appointments__booking flex w-full items-center gap-6 rounded-[28px] border border-[#e5d7c4] bg-[#fdfbf8] px-6 py-6 text-left shadow-[0_12px_30px_rgba(19,19,19,0.06)]"
        >
          <span className="portal-appointments__plus flex h-16 w-16 items-center justify-center rounded-full bg-[#cbb58a] text-2xl font-semibold text-white" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" focusable="false">
              <path d="M5 12h14M12 5v14" stroke="currentColor" strokeWidth="2.5" />
            </svg>
          </span>
          <span>
            <p className="text-lg font-semibold text-slate-900">Neuen Termin buchen</p>
            <p className="portal-appointments__booking-description mt-1 text-sm text-slate-400">Wählen Sie Ihren Wunschtermin online.</p>
          </span>
        </button>

        <section className="portal-appointments__group mt-10">
          <h2 className="text-base font-semibold text-slate-800">Anstehende Termine</h2>

          <div className="portal-section-stack mt-5 space-y-6">
            {futureAppointments.length === 0 ? (
              <div className="portal-section-card rounded-3xl border border-[#efe7da] bg-white px-6 py-8 text-center text-sm text-slate-400">
                Keine anstehenden Termine.
              </div>
            ) : (
              futureAppointments.map((appointment, index) => {
                const isChain = Boolean(appointment.appointmentInfoMap?.chainTitle);
                const chainTooltip = isChain ? t('no-chain-cancellation') : undefined;
                const chainTooltipId = `no-chain-cancellation-${appointment.id}`;

                return (
                  <div
                    key={appointment.id}
                    className={`portal-section-card portal-appointments__card flex flex-col gap-6 rounded-3xl border border-[#efe7da] bg-white p-6 shadow-[0_14px_30px_rgba(19,19,19,0.05)] md:flex-row md:items-center md:justify-between ${index === 0 ? 'portal-appointments__card--highlighted border-l-[6px] border-l-[#cbb58a]' : ''
                      }`}
                  >
                    <div className="portal-appointments__summary flex items-center gap-6">
                      <div className="portal-appointments__date flex h-24 w-24 flex-col items-center justify-center rounded-2xl border border-[#efe7da] bg-[#fcfaf7]">
                        <span className="text-xs font-semibold uppercase tracking-wide text-[#cbb58a]">
                          {formatMonth(appointment.when)}
                        </span>
                        <span className="text-3xl font-semibold text-slate-900">
                          {formatDay(appointment.when)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="portal-appointments__time flex items-center gap-2 text-sm font-medium text-slate-400">
                          <SidebarIcon name="clock" />
                          <span>{formatTime(appointment.when)} Uhr{appointment.duration ? ` · ${appointment.duration} Min.` : ''}</span>
                        </div>
                        <p className="mt-2 text-xl font-semibold text-slate-900">
                          {translateProcedureName(appointment.procedureName)}
                        </p>
                        {appointment.practitionerName ? (
                          <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                            <svg className="portal-appointments__practitioner-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <circle cx="12" cy="7" r="4" />
                              <path d="M4 21v-2a8 5 0 0 1 16 0v2H4Z" />
                            </svg>
                            <span className="truncate">{appointment.practitionerName}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="portal-appointments__actions flex flex-col gap-3 md:items-start">
                      <div className="portal-appointments__action-buttons flex items-center gap-4">
                        <span>
                          <button
                            type="button"
                            onClick={() => handleEdit(appointment.id)}
                            className="portal-section-action--gold rounded-xl bg-[#cbb58a] px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_20px_rgba(203,181,138,0.3)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                          >
                            Verschieben
                          </button>
                        </span>
                        <span
                          className="group relative inline-flex"
                          tabIndex={isChain ? 0 : undefined}
                        >
                          <button
                            type="button"
                            onClick={() => handleAbsagen(appointment)}
                            disabled={isChain}
                            aria-describedby={isChain ? chainTooltipId : undefined}
                            className="portal-section-action--neutral rounded-xl border border-[#efe7da] bg-white px-5 py-3 text-sm font-semibold text-slate-400 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Absagen
                          </button>
                          {chainTooltip ? (
                            <span
                              id={chainTooltipId}
                              role="tooltip"
                              className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 w-80 rounded-lg bg-slate-800 px-3 py-2 text-left text-xs font-medium leading-relaxed text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus:opacity-100"
                            >
                              {chainTooltip}
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={directions}
                        className="portal-directions-link inline-flex items-center gap-2 text-xs font-semibold text-[#cbb58a]"
                      >
                        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                          <path d="M10 16.2s4.2-4.4 4.2-7.6A4.2 4.2 0 0 0 5.8 8.6c0 3.2 4.2 7.6 4.2 7.6Z" />
                          <circle cx="10" cy="8.5" r="1.5" />
                        </svg>
                        <span>Anfahrt und Parken</span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="portal-appointments__group mt-12">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-800">Vergangene Termine</h2>
            <span className="rounded-full border border-[#e5d7c4] bg-white px-4 py-1 text-xs text-slate-400">
              Letzte 6 Monate
            </span>
          </div>

          <div className="portal-section-card portal-section-card--flush portal-appointments__history mt-5 rounded-3xl border border-[#efe7da] bg-white shadow-[0_14px_30px_rgba(19,19,19,0.05)]">
            <div className="grid grid-cols-[1.2fr_1.6fr_1fr_0.4fr] gap-4 border-b border-[#f1eadf] px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-300">
              <span>Datum</span>
              <span>Behandlung</span>
              <span>Status</span>
              <span></span>
            </div>
            {pastAppointments.length === 0 ? (
              <div className="px-6 py-8 text-sm text-slate-400">Keine vergangenen Termine.</div>
            ) : (
              pastAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="grid grid-cols-[1.2fr_1.6fr_1fr_0.4fr] gap-4 border-b border-[#f6f1e8] px-6 py-5 text-sm text-slate-600 last:border-b-0"
                >
                  <span>{formatDate(appointment.when)}</span>
                  <div>
                    <p className="font-semibold text-slate-800">{translateProcedureName(appointment.procedureName)}</p>
                  </div>
                  <span className="inline-flex w-fit items-center rounded-full bg-[#f6f2eb] px-4 py-1 text-xs text-slate-500">
                    Erledigt
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePastDetails(appointment.id)}
                    className="portal-section-action--neutral inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#efe7da] text-slate-300"
                  >
                    &gt;
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
